import styles from "src/styles/agGridCustom.module.scss";
import empty from "is-empty";
import {
    CUSTODY_ASSET_TYPE_KEYS,
    CUSTODY_ASSET_TYPES,
    DEFAULT_CCY_OPTIONS,
    HUMANIZED_TX_TYPES,
    TX_TYPE_VALUES,
} from "src/redux/Confirmations/constants";
import * as Validators from "src/utils/validators";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { store } from "src/index";
import { numberOrDash } from "src/utils/number";
import DropdownItemRenderer from "src/components/common/AgGrid/DropdownItemRenderer";

export const listDefaultColDef = {
    resizable: false,
    sortable: false,
    filter: false,
};

const valueFormatter = (params) => {
    if (
        params.colDef.colId.startsWith("Create") &&
        !params.data.hasOwnProperty(params.colDef.field)
    )
        return "";

    if (
        params.colDef.colId.startsWith("Details") &&
        params.colDef.field === "dividend"
    ) {
        return numberOrDash({ value: params.value });
    }

    if (params.colDef.field === "txType") {
        return HUMANIZED_TX_TYPES[params.value] || HUMANIZED_TX_TYPES.ANY;
    }

    if (params.colDef.field === "callable") {
        return empty(params.value) ? "-" : params.value ? "Y" : "N";
    }

    if (["ksdAccount", "ksfcAccount"].includes(params.colDef.field)) {
        return empty(params.value)
            ? "-"
            : `${params.value.name}-${params.value.accountNo}`;
    }
    return params.value || "-";
};

const cellClass = [styles.agCenterAlign];

const getNewValueCellError = (params) => {
    const displayError = SettingsSelectors.DisplayListErrors(store.getState());
    if (!displayError) return;
    const currentSettings = SettingsSelectors.ListSettings(store.getState());
    const dynamicAndClientPoolList = SettingsSelectors.DynamicAndClientPoolList(store.getState());
    const { "enable-multi-account-code-support": isAccountSelectable } =
        SettingsSelectors.FeatureFlags(store.getState());
    const errors = Validators.getListValidationErrors(
        params.data,
        currentSettings,
        dynamicAndClientPoolList,
        isAccountSelectable
    );
    return (
        errors && errors.some((error) => error.startsWith(params.colDef.field))
    );
};

const cellClassRules = {
    [styles.agNewValueCellError]: (params) => getNewValueCellError(params),
    [styles.agEditableBlack]: (params) => !getNewValueCellError(params),
};

export const getCreateListColumnDefs = ({ isAccountSelectable }) => {
    let columns = [
        {
            headerName: "List Name",
            colId: "Create List Name",
            field: "name",
            width: 106,
            minWidth: 106,
            flex: 106,
            editable: true,
            cellClassRules,
            cellClass,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Internal\nFund Code",
            colId: "Create Internal Fund Code",
            field: "internalFundCode",
            width: 88,
            minWidth: 88,
            flex: 88,
            editable: true,
            cellClassRules,
            cellClass,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        isAccountSelectable
            ? {
                  headerName: "KSD Account",
                  colId: "Create KSD Account",
                  field: "ksdAccount",
                  width: 150,
                  minWidth: 150,
                  flex: 150,
                  editable: true,
                  cellClassRules,
                  cellClass,
                  headerClass: styles.agHeaderCenterAlign,
                  menuTabs: [],
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: (params) => ({
                      values: [
                          {},
                          ...params.context.accounts.filter(
                              (a) => a.type === "KSD"
                          ),
                      ],
                      formatValue: (value) =>
                          empty(value)
                              ? "-"
                              : `${value.name}-${value.accountNo}`,
                      cellRenderer: DropdownItemRenderer,
                  }),
                  valueFormatter,
              }
            : {},

        {
            headerName: "SLB Num\n(대차계정번호)",
            colId: "Create SLB Number",
            field: "slbNumber",
            width: 96,
            minWidth: 96,
            flex: 96,
            editable: true,
            cellClassRules,
            cellClass,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "KSD Fund Code\n(예탁원 펀드 코드)",
            colId: "Create KSD Fund Code",
            field: "ksdFundCode",
            width: 112,
            minWidth: 112,
            flex: 112,
            editable: true,
            cellClassRules,
            cellClass,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        isAccountSelectable
            ? {
                  headerName: "KSFC Account",
                  colId: "Create KSFC Account",
                  field: "ksfcAccount",
                  width: 150,
                  minWidth: 150,
                  flex: 150,
                  editable: true,
                  cellClassRules,
                  cellClass,
                  headerClass: styles.agHeaderCenterAlign,
                  menuTabs: [],
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: (params) => ({
                      values: [
                          {},
                          ...params.context.accounts.filter(
                              (a) => a.type === "KSFC"
                          ),
                      ],
                      formatValue: (value) =>
                          empty(value)
                              ? "-"
                              : `${value.name}-${value.accountNo}`,
                      cellRenderer: DropdownItemRenderer,
                  }),
                  valueFormatter,
              }
            : {},
        {
            headerName: "KSFC Fund Code\n(한국증권금융 펀드 코드)",
            colId: "Create KSFC Fund Code",
            field: "ksfcFundCode",
            width: 148,
            minWidth: 148,
            flex: 148,
            editable: true,
            cellClassRules,
            cellClass,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Custody Asset Type\n(예탁재산구분 코드)",
            colId: "Create Custody Asset Type",
            field: "custodyAssetType",
            width: 155,
            minWidth: 155,
            flex: 155,
            editable: true,
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: {
                values: CUSTODY_ASSET_TYPE_KEYS,
                formatValue: (value) => CUSTODY_ASSET_TYPES[value] || "-",
                cellRenderer: DropdownItemRenderer,
            },
            valueFormatter,
            cellClassRules,
            cellClass,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Type\n(구분)",
            colId: "Create TX Type",
            field: "txType",
            width: 96,
            minWidth: 96,
            flex: 96,
            editable: true,
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: {
                values: Object.keys(TX_TYPE_VALUES).map(
                    (key) => TX_TYPE_VALUES[key]
                ),
                formatValue: (value) =>
                    HUMANIZED_TX_TYPES[value] || HUMANIZED_TX_TYPES.ANY,
                cellRenderer: DropdownItemRenderer,
            },
            valueFormatter,
            cellClassRules,
            cellClass,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Callable",
            colId: "Create Callable",
            field: "callable",
            width: 56,
            minWidth: 56,
            flex: 56,
            editable: true,
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellClassRules,
            cellClass,
            headerClass: styles.agHeaderCenterAlign,
            cellEditorParams: {
                values: [true, false],
                formatValue: (value) => (value ? "Y" : "N"),
                cellRenderer: DropdownItemRenderer,
            },
            valueFormatter,
            menuTabs: [],
        },
        {
            headerName: "Coll\nType",
            colId: "Create Coll Type",
            field: "collType",
            width: 50,
            minWidth: 50,
            flex: 50,
            editable: true,
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            headerClass: styles.agHeaderCenterAlign,
            cellEditorParams: {
                values: ["CA", "NC", "CP"],
                formatValue: (value) => (empty(value) ? "-" : value),
                cellRenderer: DropdownItemRenderer,
            },
            cellClassRules,
            cellClass,
            valueFormatter,
            menuTabs: [],
        },
        {
            headerName: "Coll\nCCY",
            colId: "Create Coll Ccy",
            field: "collCcy",
            width: 58,
            minWidth: 58,
            flex: 58,
            editable: true,
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            headerClass: styles.agHeaderCenterAlign,
            cellEditorParams: {
                values: DEFAULT_CCY_OPTIONS,
                formatValue: (value) => (empty(value) ? "-" : value),
                cellRenderer: DropdownItemRenderer,
            },
            cellClassRules,
            cellClass,
            valueFormatter,
            menuTabs: [],
        },
        {
            headerName: "Div (%)",
            colId: "Create Div",
            field: "dividend",
            width: 54,
            minWidth: 54,
            flex: 54,
            editable: true,
            headerClass: styles.agHeaderCenterAlign,
            cellClassRules,
            cellClass,
            valueFormatter,
            menuTabs: [],
        },
    ];
    return columns.filter((col) => !empty(col));
};

export const getDetailListColumnDefs = ({
    isAccountSelectable,
    isListLevelAvailability,
}) => {
    let columns = [
        {
            headerName: "List Name",
            colId: "Details List Name",
            field: "name",
            width: 132,
            minWidth: 132,
            cellClass: [styles.agCenterAlign, styles.agListDetail],
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Internal\nFund Code",
            colId: "Details Internal Fund Code",
            field: "internalFundCode",
            width: 108,
            minWidth: 108,
            cellClass: [styles.agCenterAlign, styles.agListDetail],
            valueFormatter,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        isAccountSelectable
            ? {
                  headerName: "KSD Account",
                  colId: "Details KSD Account",
                  field: "ksdAccount",
                  width: 150,
                  minWidth: 150,
                  cellClass: [styles.agCenterAlign, styles.agListDetail],
                  valueFormatter,
                  headerClass: styles.agHeaderCenterAlign,
                  menuTabs: [],
              }
            : {},
        {
            headerName: "SLB Number",
            colId: "Details SLB Number",
            field: "slbNumber",
            width: 78,
            minWidth: 78,
            cellClass: [styles.agCenterAlign, styles.agListDetail],
            valueFormatter,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "KSD Fund Code",
            colId: "Details KSD Fund Code",
            field: "ksdFundCode",
            width: 120,
            minWidth: 120,
            cellClass: [styles.agCenterAlign, styles.agListDetail],
            valueFormatter,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        isAccountSelectable
            ? {
                  headerName: "KSFC Account",
                  colId: "Details KSFC Account",
                  field: "ksfcAccount",
                  width: 150,
                  minWidth: 150,
                  cellClass: [styles.agCenterAlign, styles.agListDetail],
                  valueFormatter,
                  headerClass: styles.agHeaderCenterAlign,
                  menuTabs: [],
              }
            : {},
        {
            headerName: "KSFC Fund Code",
            colId: "Details KSFC Fund Code",
            field: "ksfcFundCode",
            width: 120,
            minWidth: 120,
            cellClass: [styles.agCenterAlign, styles.agListDetail],
            valueFormatter,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Custody\nAsset Type",
            colId: "Details Custody Asset Type",
            field: "custodyAssetType",
            width: 80,
            minWidth: 80,
            cellClass: [styles.agCenterAlign, styles.agListDetail],
            valueFormatter,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Type",
            colId: "Details TX Type",
            field: "txType",
            width: 100,
            minWidth: 100,
            valueFormatter,
            cellClass: [styles.agCenterAlign, styles.agListDetail],
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Callable",
            colId: "Details Callable",
            field: "callable",
            width: 60,
            minWidth: 60,
            valueFormatter,
            cellClass: [styles.agCenterAlign, styles.agListDetail],
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Coll\nType",
            colId: "Details Coll Type",
            field: "collType",
            width: 54,
            minWidth: 54,
            valueFormatter,
            cellClass: [styles.agCenterAlign, styles.agListDetail],
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Coll\nCCY",
            colId: "Details Coll Ccy",
            field: "collCcy",
            width: 55,
            minWidth: 55,
            valueFormatter,
            cellClass: [styles.agCenterAlign, styles.agListDetail],
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Div (%)",
            colId: "Details Div",
            field: "dividend",
            width: 58,
            minWidth: 58,
            cellClass: [styles.agCenterAlign, styles.agListDetail],
            headerClass: styles.agHeaderCenterAlign,
            valueFormatter,
            menuTabs: [],
        },
        isListLevelAvailability
            ? {
                  headerName: "Send\nto",
                  colId: "Details Availability",
                  field: "advertiseAvailabilitySettings",
                  cellRenderer: "availabilityButton",
                  width: 65,
                  minWidth: 65,
                  cellClass: [styles.agCenterAlign, styles.agListDetail],
                  headerClass: styles.agHeaderCenterAlign,
                  valueFormatter,
                  menuTabs: [],
              }
            : {},
        {
            headerName: "Delete",
            colId: "Details Delete",
            cellRenderer: "deleteButton",
            width: 56,
            minWidth: 56,
            cellClass: [styles.agCenterAlign, styles.agListDetail],
            headerClass: styles.agHeaderCenterAlign,
            valueFormatter,
            menuTabs: [],
        },
    ];
    return columns.filter((col) => !empty(col));
};
